import { KITS } from "@golazo/shared";
import { t } from "../i18n/index.js";
import { tableOf, type CareerEvent } from "../career/career.js";
import { DIVISIONS, MATCHDAYS, type Career, type PhoneMessage } from "../career/model.js";
import { escapeHtml, ordinal, phoneText, senderOf } from "./careerText.js";
import "./careerMatchResult.css";

/**
 * El final de un partido de carrera: lo que te llevas de el.
 *
 * La nota, como va la tabla, lo que se ha ganado (camisetas, titulos) y lo
 * primero que suena en el movil al acabar. El movil entero, la tabla y el
 * calendario viven en la pantalla de la carrera; aqui va solo el trago corto.
 */
export interface CareerResultInput {
  career: Career;
  events: readonly CareerEvent[];
  /** Mensajes que ha traido este partido (los nuevos del movil). */
  messages: readonly PhoneMessage[];
  /** Lo desbloqueado de verdad ahora (sin lo que ya tenias). */
  unlocked: { kits: readonly string[]; custom: boolean };
}

const MAX_MESSAGES = 3;

function kitSwatch(kitId: string): string {
  const kit = KITS[kitId];
  if (!kit) return "";
  const hex = (n: number): string => `#${n.toString(16).padStart(6, "0")}`;
  return `<i class="cmr-kit" style="--a:${hex(kit.primary)};--b:${hex(kit.secondary)}" aria-hidden="true"></i>`;
}

export function careerResultHtml(input: CareerResultInput): string {
  const { career, events, messages, unlocked } = input;
  // El partido que se acaba de jugar es la ultima linea de la temporada que
  // lo contiene: si ha cerrado la temporada, la del historial manda.
  const summary = events.find((e) => e.type === "seasonEnd");
  const line = career.season.lines.at(-1);
  const tier = summary?.type === "seasonEnd" ? summary.summary.tier : career.season.tier;
  const played = summary?.type === "seasonEnd" ? MATCHDAYS : career.season.matchday;
  const division = t(DIVISIONS[tier]!.name);

  const table = tableOf(career, tier);
  const myRowIndex = table.findIndex((r) => r.club === career.season.club);
  const position =
    summary?.type === "seasonEnd" ? summary.summary.position : myRowIndex >= 0 ? myRowIndex + 1 : null;
  const points = summary?.type === "seasonEnd" ? summary.summary.points : table[myRowIndex]?.points ?? 0;

  const rows: string[] = [];
  if (line && !line.absent) {
    rows.push(`<div class="cmr-row"><span>${t("Tu nota")}</span><b class="cmr-grade">${line.grade.toFixed(1)}</b></div>`);
  }
  // El encargo del mister: cumplido o no, con su marca (ver `phoneTalk.ts`).
  const verdict = messages.find((m) => m.card === "objective" && m.params?.st !== undefined);
  if (verdict && typeof verdict.params?.objective === "string") {
    const done = verdict.params.st === 1;
    const what = t(verdict.params.objective.slice(1), { n: Number(verdict.params.n ?? 0) });
    rows.push(
      `<div class="cmr-row cmr-objective ${done ? "is-done" : "is-failed"}"><span>${t("Encargo del míster")}</span><b>${done ? "✓" : "✗"} ${escapeHtml(what)}</b></div>`,
    );
  }
  if (position !== null) {
    rows.push(
      `<div class="cmr-row"><span>${t("Clasificación")}</span><b>${ordinal(position)} · ${points === 1 ? t("1 punto") : t("{n} pts", { n: points })}</b></div>`,
    );
  }

  const news: string[] = [];
  for (const e of events) {
    if (e.type === "title") news.push(`🏆 ${t("¡Campeones de {division}!", { division: t(DIVISIONS[e.tier]!.name) })}`);
    if (e.type === "promoted") news.push(`⬆️ ${t("¡Ascenso a {division}!", { division: t(DIVISIONS[e.tier]!.name) })}`);
    if (e.type === "relegated") news.push(`⬇️ ${t("Descenso a {division}.", { division: t(DIVISIONS[e.tier]!.name) })}`);
  }
  for (const kitId of unlocked.kits) {
    const label = KITS[kitId] ? t(KITS[kitId]!.label) : kitId;
    news.push(`${kitSwatch(kitId)} ${escapeHtml(t("Equipación nueva: {kit}", { kit: label }))}`);
  }
  if (unlocked.custom) news.push(`✂️ ${t("Ya puedes diseñar tu equipación «A medida».")}`);

  const phone = messages
    .filter((m) => m.from !== "system")
    .slice(0, MAX_MESSAGES)
    .map((m) => {
      const who = senderOf(m.from);
      return `<li class="cmr-msg is-${m.from}"><span class="cmr-who"><i aria-hidden="true">${who.icon}</i>${escapeHtml(who.label)}</span><p>${escapeHtml(phoneText(m))}</p></li>`;
    })
    .join("");

  return `
    <section class="rs-block cmr">
      <header class="rs-block-head">
        <span>${escapeHtml(division)} · ${t("Jornada {n} de {total}", { n: played, total: MATCHDAYS })}</span>
      </header>
      ${rows.join("")}
      ${news.length ? `<ul class="cmr-news">${news.map((n) => `<li>${n}</li>`).join("")}</ul>` : ""}
      ${phone ? `<ul class="cmr-phone" aria-label="${t("Tu móvil")}">${phone}</ul>` : ""}
    </section>`;
}
